import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  listEligibleNotificationUsers,
  listNotificationHistory,
  listNotificationEventConfigurations,
  previewNotificationTemplate,
  restoreNotificationTemplateDefault,
  updateNotificationEventConfiguration,
} from "../../api/notificationConfiguration.api";
import Button from "../../ui/Button";
import ModalA11yWrapper from "../../ui/ModalA11yWrapper";
import { colors, radius, shadows } from "../../ui/tokens";

const MODULES = ["All", "Risks", "Controls", "Actions", "Incidents", "Audits", "Policies", "KPIs", "Board / Governance"];
const ACCESS_PROFILES = ["ADMIN", "MANAGER", "CONTRIBUTOR", "VIEWER", "AUDITOR"];
const RECIPIENT_LABELS = {
  record_owner: "Record owner",
  assigned_user: "Assigned user",
  reviewer: "Reviewer / approver",
  creator: "Creator",
  responsible_manager: "Responsible manager",
  relevant_admin_role: "Relevant administrator role",
  access_profile: "Access profile",
  named_users: "Selected named users",
  escalation_target: "Escalation target",
  committee_attendees: "Committee attendees",
  mentioned_user: "Mentioned user",
};

const panel = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  boxShadow: shadows.xs,
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  border: `1px solid ${colors.borderStrong}`,
  borderRadius: radius.sm,
  padding: "9px 10px",
  color: colors.textPrimary,
  background: colors.surface,
  font: "inherit",
};

const responseData = (response) => response?.data ?? response;
const extractError = (error, fallback) =>
  error?.response?.data?.detail || error?.detail || error?.message || error?.responseBody?.detail || fallback;

const normalizedRules = (rules = []) =>
  rules.map((rule) => ({
    type: rule.type,
    user_ids: rule.user_ids || [],
    access_profiles: rule.access_profiles || [],
  }));

const normalizedTiming = (timing = {}) => ({
  reminder_offsets_days: timing.reminder_offsets_days || [],
  notify_on_due_date: Boolean(timing.notify_on_due_date),
  overdue_enabled: Boolean(timing.overdue_enabled),
  overdue_repeat_interval_days: timing.overdue_repeat_interval_days ?? "",
  escalations: (timing.escalations || []).map((rule) => ({
    days_overdue: rule.days_overdue,
    recipients: normalizedRules(rule.recipients),
  })),
});

const eventDraft = (event) =>
  event
    ? {
        enabled: Boolean(event.enabled),
        email_enabled: Boolean(event.email_enabled),
        in_app_enabled: Boolean(event.in_app_enabled),
        recipients: normalizedRules(event.recipients),
        timing: normalizedTiming(event.timing),
        subject_template: event.template.subject_template,
        body_template: event.template.body_template,
      }
    : null;

const configurationPayload = (draft) => ({
  ...draft,
  timing: {
    ...draft.timing,
    overdue_repeat_interval_days: draft.timing.overdue_repeat_interval_days || null,
  },
});

const sameDraft = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const recipientSummary = (recipients = []) =>
  recipients.length ? recipients.map((rule) => RECIPIENT_LABELS[rule.type] || rule.type).join(" + ") : "No recipients";

const timingSummary = (timing = {}) => {
  const parts = [];
  if (timing.reminder_offsets_days?.length) parts.push(`${timing.reminder_offsets_days.join(", ")}d before`);
  if (timing.notify_on_due_date) parts.push("On due date");
  if (timing.overdue_enabled) {
    parts.push(timing.overdue_repeat_interval_days ? `Overdue every ${timing.overdue_repeat_interval_days}d` : "Initial overdue");
  }
  if (timing.escalations?.length) parts.push(`Escalate ${timing.escalations.map((rule) => `${rule.days_overdue}d`).join(", ")}`);
  return parts.length ? parts.join(" · ") : "Not configured";
};

const invalidTemplateTokens = (subject, body, supportedVariables) => {
  const content = `${subject}\n${body}`;
  const tokens = content.match(/{{[^{}]*}}/g) || [];
  const invalid = tokens.filter((token) => {
    const match = token.match(/^{{([A-Za-z][A-Za-z0-9_]*)}}$/);
    return !match || !supportedVariables.includes(match[1]);
  });
  const stripped = content.replace(/{{[^{}]*}}/g, "");
  if (stripped.includes("{{") || stripped.includes("}}") || content.includes("{%") || content.includes("%}")) {
    invalid.push("unsupported expression");
  }
  return [...new Set(invalid)];
};

function StatusToggle({ label, checked, disabled = false, disabledReason, onChange }) {
  const stateText = checked ? "On" : "Off";
  const visibleText = label === "Status" ? (checked ? "Enabled" : "Disabled") : `${label}: ${stateText}`;
  return (
    <Button
      type="button"
      variant="secondary"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      aria-label={`${label}: ${label === "Status" ? (checked ? "Enabled" : "Disabled") : stateText}`}
      data-tone={checked ? "success" : "danger"}
      title={disabled ? disabledReason : `Turn ${label.toLowerCase()} ${checked ? "off" : "on"}`}
      style={{
        minHeight: 32,
        padding: "5px 9px",
        borderRadius: radius.pill,
        borderColor: checked ? colors.successBorder : colors.dangerBorder,
        background: checked ? colors.successSubtle : colors.dangerSubtle,
        color: checked ? colors.success : colors.danger,
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: ".03em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true">{checked ? "✓" : "×"}</span>
      {visibleText}
    </Button>
  );
}

function EventSettingsEditor({ event, draft, eligibleUsers, onChange, onClose }) {
  const updateRule = (index, changes) => onChange({
    ...draft,
    recipients: draft.recipients.map((rule, ruleIndex) => ruleIndex === index ? { ...rule, ...changes } : rule),
  });
  const defaultRecipient = () => ({ type: event.permitted_recipient_types[0], user_ids: [], access_profiles: [] });

  return (
    <div id={`settings-${event.event_key}`} aria-label={`Settings for ${event.name}`} style={{ display: "grid", gap: 18, padding: 16, background: colors.surfaceSubtle }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "start" }}>
        <div>
          <strong>{event.name}</strong>
          <p style={{ color: colors.textMuted, margin: "5px 0 0", lineHeight: 1.5 }}>{event.description}</p>
          <div style={{ color: colors.textSecondary, fontSize: 12, marginTop: 4 }}><strong>Triggered when:</strong> {event.trigger}</div>
          {event.implementation_status === "configuration_only" ? <div style={{ color: colors.warning, marginTop: 7, fontSize: 12 }}>Configuration is ready; domain trigger integration is scheduled for a later delivery ticket.</div> : null}
          {event.implementation_status === "email_outbox" ? <div style={{ color: colors.success, marginTop: 7, fontSize: 12 }}>This event is connected to the durable email outbox.</div> : null}
          {event.implementation_status === "scheduled_outbox" ? <div style={{ color: colors.success, marginTop: 7, fontSize: 12 }}>This event is evaluated by the notification scheduler and delivered through the durable outbox.</div> : null}
        </div>
        <Button type="button" variant="ghost" onClick={onClose} aria-label={`Close settings for ${event.name}`}>Close</Button>
      </div>

      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <h4 style={{ margin: 0 }}>Recipients</h4>
          <Button type="button" variant="secondary" disabled={!event.permitted_recipient_types.length} onClick={() => onChange({ ...draft, recipients: [...draft.recipients, defaultRecipient()] })}>Add rule</Button>
        </div>
        <p style={{ color: colors.textMuted, fontSize: 12 }}>Recipient rules are tenant scoped. Named users must already have access to the event&apos;s module.</p>
        <div style={{ display: "grid", gap: 10 }}>
          {draft.recipients.map((rule, index) => (
            <div key={`${rule.type}-${index}`} style={{ ...panel, padding: 12, display: "grid", gap: 10 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <select aria-label={`Recipient rule ${index + 1}`} value={rule.type} onChange={(changeEvent) => updateRule(index, { type: changeEvent.target.value, user_ids: [], access_profiles: [] })} style={inputStyle}>
                  {event.permitted_recipient_types.map((type) => <option key={type} value={type}>{RECIPIENT_LABELS[type] || type}</option>)}
                </select>
                {draft.recipients.length > 1 ? <Button type="button" variant="ghost" onClick={() => onChange({ ...draft, recipients: draft.recipients.filter((_, ruleIndex) => ruleIndex !== index) })}>Remove</Button> : null}
              </div>
              {rule.type === "named_users" ? <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>{eligibleUsers.map((user) => <label key={user.id} style={{ fontSize: 13 }}><input type="checkbox" checked={rule.user_ids.includes(user.id)} onChange={(changeEvent) => updateRule(index, { user_ids: changeEvent.target.checked ? [...rule.user_ids, user.id] : rule.user_ids.filter((id) => id !== user.id) })} /> {user.username} ({user.email})</label>)}</div> : null}
              {rule.type === "access_profile" ? <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>{ACCESS_PROFILES.map((profile) => <label key={profile} style={{ fontSize: 13 }}><input type="checkbox" checked={rule.access_profiles.includes(profile)} onChange={(changeEvent) => updateRule(index, { access_profiles: changeEvent.target.checked ? [...rule.access_profiles, profile] : rule.access_profiles.filter((value) => value !== profile) })} /> {profile}</label>)}</div> : null}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

function TimingEditor({ event, draft, eligibleUsers, onChange, onClose, onSave, saving, dirty }) {
  const updateTiming = (changes) => onChange({ ...draft, timing: { ...draft.timing, ...changes } });
  const updateEscalation = (index, changes) => updateTiming({
    escalations: draft.timing.escalations.map((rule, ruleIndex) => ruleIndex === index ? { ...rule, ...changes } : rule),
  });
  const updateEscalationRecipient = (escalationIndex, recipientIndex, changes) => {
    const escalation = draft.timing.escalations[escalationIndex];
    updateEscalation(escalationIndex, {
      recipients: escalation.recipients.map((rule, ruleIndex) => ruleIndex === recipientIndex ? { ...rule, ...changes } : rule),
    });
  };
  const defaultRecipient = () => ({ type: event.permitted_recipient_types[0], user_ids: [], access_profiles: [] });

  return (
    <div id={`timing-${event.event_key}`} aria-label={`Timing and escalation for ${event.name}`} style={{ display: "grid", gap: 14, padding: 16, background: colors.surfaceSubtle }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
        <div><h3 style={{ margin: 0 }}>{event.name}: Timing / Escalation</h3><p style={{ color: colors.textMuted, fontSize: 12, margin: "6px 0 0" }}>Times are evaluated against the record&apos;s due date in UTC. Each due-date cycle and threshold is emitted only once.</p></div>
        <Button type="button" variant="ghost" onClick={onClose} aria-label={`Close timing for ${event.name}`}>Close</Button>
      </div>
      <div style={{ ...panel, padding: 14, display: "grid", gap: 12 }}>
        {event.timing_modes.includes("advance") ? <label style={{ display: "grid", gap: 5, fontWeight: 700 }}>Reminder offsets (days before due)<input aria-label="Reminder offsets in days" value={draft.timing.reminder_offsets_days.join(", ")} placeholder="30, 14, 7, 1" onChange={(changeEvent) => updateTiming({ reminder_offsets_days: changeEvent.target.value.split(",").map((value) => Number(value.trim())).filter((value) => Number.isInteger(value) && value > 0) })} style={inputStyle} /></label> : null}
        {event.timing_modes.includes("due") ? <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 700 }}><input type="checkbox" checked={draft.timing.notify_on_due_date} onChange={(changeEvent) => updateTiming({ notify_on_due_date: changeEvent.target.checked })} />Notify on the due date</label> : null}
        {event.timing_modes.includes("overdue") ? <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 700 }}><input type="checkbox" aria-label="Notify when overdue" checked={draft.timing.overdue_enabled} onChange={(changeEvent) => updateTiming({ overdue_enabled: changeEvent.target.checked, overdue_repeat_interval_days: changeEvent.target.checked ? draft.timing.overdue_repeat_interval_days : "" })} />Notify when overdue</label> : null}
        {event.timing_modes.includes("repeat") && draft.timing.overdue_enabled ? <label style={{ display: "grid", gap: 5, fontWeight: 700, maxWidth: 320 }}>Repeat every (days, optional)<input type="number" min="1" max="365" aria-label="Overdue repeat interval" value={draft.timing.overdue_repeat_interval_days} onChange={(changeEvent) => updateTiming({ overdue_repeat_interval_days: changeEvent.target.value ? Number(changeEvent.target.value) : "" })} style={inputStyle} /></label> : null}
        {event.timing_modes.includes("escalation") ? <div style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}><strong>Escalation thresholds</strong><Button type="button" variant="secondary" onClick={() => updateTiming({ escalations: [...draft.timing.escalations, { days_overdue: 7, recipients: [{ type: "responsible_manager", user_ids: [], access_profiles: [] }] }] })}>Add escalation</Button></div>
          {draft.timing.escalations.map((escalation, index) => <div key={index} style={{ padding: 12, background: colors.surfaceMuted, borderRadius: radius.sm, display: "grid", gap: 9 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "end" }}><label style={{ fontSize: 12, fontWeight: 700, maxWidth: 180 }}>Days overdue<input aria-label={`Escalation ${index + 1} days overdue`} type="number" min="1" max="3650" value={escalation.days_overdue} onChange={(changeEvent) => updateEscalation(index, { days_overdue: Number(changeEvent.target.value) })} style={inputStyle} /></label><div style={{ display: "flex", gap: 6 }}><Button type="button" variant="secondary" onClick={() => updateEscalation(index, { recipients: [...escalation.recipients, defaultRecipient()] })}>Add recipient rule</Button><Button type="button" variant="ghost" onClick={() => updateTiming({ escalations: draft.timing.escalations.filter((_, ruleIndex) => ruleIndex !== index) })}>Remove escalation</Button></div></div>
            {escalation.recipients.map((rule, recipientIndex) => <div key={`${rule.type}-${recipientIndex}`} style={{ display: "grid", gap: 7 }}>
              <div style={{ display: "flex", gap: 8 }}><label style={{ fontSize: 12, fontWeight: 700, flex: 1 }}>Recipients<select aria-label={`Escalation ${index + 1} recipient rule ${recipientIndex + 1}`} value={rule.type} onChange={(changeEvent) => updateEscalationRecipient(index, recipientIndex, { type: changeEvent.target.value, user_ids: [], access_profiles: [] })} style={inputStyle}>{event.permitted_recipient_types.map((type) => <option key={type} value={type}>{RECIPIENT_LABELS[type] || type}</option>)}</select></label>{escalation.recipients.length > 1 ? <Button type="button" variant="ghost" onClick={() => updateEscalation(index, { recipients: escalation.recipients.filter((_, ruleIndex) => ruleIndex !== recipientIndex) })}>Remove rule</Button> : null}</div>
              {rule.type === "named_users" ? <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>{eligibleUsers.map((user) => <label key={user.id} style={{ fontSize: 13 }}><input type="checkbox" checked={rule.user_ids.includes(user.id)} onChange={(changeEvent) => updateEscalationRecipient(index, recipientIndex, { user_ids: changeEvent.target.checked ? [...rule.user_ids, user.id] : rule.user_ids.filter((id) => id !== user.id) })} /> {user.username} ({user.email})</label>)}</div> : null}
              {rule.type === "access_profile" ? <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>{ACCESS_PROFILES.map((profile) => <label key={profile} style={{ fontSize: 13 }}><input type="checkbox" checked={rule.access_profiles.includes(profile)} onChange={(changeEvent) => updateEscalationRecipient(index, recipientIndex, { access_profiles: changeEvent.target.checked ? [...rule.access_profiles, profile] : rule.access_profiles.filter((value) => value !== profile) })} /> {profile}</label>)}</div> : null}
            </div>)}
          </div>)}
        </div> : null}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}><Button type="button" variant="secondary" onClick={onClose}>Cancel</Button><Button type="button" variant="primary" disabled={!dirty || saving} loading={saving} onClick={onSave}>{saving ? "Saving..." : "Save timing"}</Button></div>
    </div>
  );
}

const HISTORY_STATUS_LABELS = {
  PENDING: "Pending",
  PROCESSING: "Processing",
  DELIVERED: "Delivered",
  RETRY_PENDING: "Retry pending",
  FAILED: "Failed",
  SKIPPED: "Skipped",
  IN_APP_CREATED: "In-app created",
};

const dateInputValue = (date) => {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
};
const presetDates = (days) => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { from: dateInputValue(from), to: dateInputValue(to) };
};
const displayDateTime = (value) => value ? new Date(value).toLocaleString() : "Not recorded";
const occurrenceLabel = (occurrence) => {
  if (!occurrence) return null;
  if (occurrence.kind === "advance" && occurrence.offset_days != null) return `${occurrence.offset_days} days before due`;
  if (occurrence.kind === "overdue_initial") return "Initial overdue";
  if (occurrence.kind === "overdue_repeat") return "Recurring overdue";
  if (occurrence.kind === "escalation" && occurrence.escalation_days != null) return `Escalation at ${occurrence.escalation_days} days overdue`;
  return String(occurrence.kind || "Scheduled occurrence").replaceAll("_", " ");
};

function HistoryStatus({ status }) {
  const tone = status === "DELIVERED" || status === "IN_APP_CREATED" ? "success" : status === "FAILED" ? "danger" : "warning";
  const palette = tone === "success"
    ? { color: colors.success, border: colors.successBorder, background: colors.successSubtle }
    : tone === "danger"
      ? { color: colors.danger, border: colors.dangerBorder, background: colors.dangerSubtle }
      : { color: colors.warning, border: colors.warningBorder, background: colors.warningSubtle };
  return <span data-tone={tone} style={{ display: "inline-block", borderRadius: radius.pill, border: `1px solid ${palette.border}`, background: palette.background, color: palette.color, padding: "4px 8px", fontSize: 11, fontWeight: 800, whiteSpace: "nowrap" }}>{HISTORY_STATUS_LABELS[status] || status}</span>;
}

const recipientOptionLabel = (option) => option.email_masked
  ? `${option.display_name} — ${option.email_masked}`
  : option.display_name;

function RecipientCombobox({ options, value, onChange }) {
  const uniqueOptions = useMemo(() => Array.from(new Map(options.map((option) => [option.recipient_user_id, option])).values()), [options]);
  const selected = uniqueOptions.find((option) => option.recipient_user_id === value) || null;
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const listboxId = "notification-history-recipient-options";

  useEffect(() => {
    if (selected) setQuery(recipientOptionLabel(selected));
    else if (value == null) setQuery("");
  }, [selected, value]);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = uniqueOptions.filter((option) => !normalizedQuery || recipientOptionLabel(option).toLowerCase().includes(normalizedQuery));
  const choices = [null, ...filteredOptions];
  const choose = (option) => {
    onChange(option?.recipient_user_id || null);
    setQuery(option ? recipientOptionLabel(option) : "");
    setOpen(false);
    setActiveIndex(0);
  };
  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => Math.min(current + 1, choices.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === "Enter" && open) {
      event.preventDefault();
      choose(choices[activeIndex]);
    } else if (event.key === "Escape") {
      setOpen(false);
      setQuery(selected ? recipientOptionLabel(selected) : "");
    }
  };

  return (
    <div onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }} style={{ position: "relative" }}>
      <div style={{ display: "flex", gap: 5 }}>
        <input
          aria-label="History recipient"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={open ? `history-recipient-option-${activeIndex}` : undefined}
          value={query}
          placeholder="All recipients"
          onFocus={() => { setOpen(true); setActiveIndex(0); }}
          onChange={(event) => { setQuery(event.target.value); setOpen(true); setActiveIndex(0); }}
          onKeyDown={handleKeyDown}
          style={inputStyle}
        />
        {(value != null || query) ? <Button type="button" variant="ghost" aria-label="Clear recipient" onClick={() => choose(null)}>Clear</Button> : null}
      </div>
      {open ? <div id={listboxId} role="listbox" aria-label="Recipient options" style={{ position: "absolute", zIndex: 10, top: "calc(100% + 4px)", left: 0, right: 0, maxHeight: 240, overflowY: "auto", ...panel, padding: 4 }}>
        <button id="history-recipient-option-0" type="button" role="option" aria-selected={value == null} onMouseDown={(event) => event.preventDefault()} onClick={() => choose(null)} style={{ ...inputStyle, border: 0, textAlign: "left", cursor: "pointer", background: activeIndex === 0 ? colors.surfaceMuted : colors.surface }}>All recipients</button>
        {filteredOptions.map((option, index) => <button key={option.recipient_user_id} id={`history-recipient-option-${index + 1}`} type="button" role="option" aria-selected={option.recipient_user_id === value} onMouseDown={(event) => event.preventDefault()} onMouseEnter={() => setActiveIndex(index + 1)} onClick={() => choose(option)} style={{ ...inputStyle, border: 0, textAlign: "left", cursor: "pointer", background: activeIndex === index + 1 ? colors.surfaceMuted : colors.surface }}>{recipientOptionLabel(option)}</button>)}
        {!uniqueOptions.length ? <div style={{ padding: 10, color: colors.textMuted }}>No recipients available</div> : filteredOptions.length === 0 ? <div style={{ padding: 10, color: colors.textMuted }}>No matching recipients</div> : null}
      </div> : null}
    </div>
  );
}

function NotificationHistory({ events }) {
  const initialDates = useMemo(() => presetDates(7), []);
  const [range, setRange] = useState("7");
  const [fromDate, setFromDate] = useState(initialDates.from);
  const [toDate, setToDate] = useState(initialDates.to);
  const [module, setModule] = useState("");
  const [eventKey, setEventKey] = useState("");
  const [channel, setChannel] = useState("");
  const [status, setStatus] = useState("");
  const [recipientUserId, setRecipientUserId] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [history, setHistory] = useState({ items: [], recipient_options: [], total: 0, page: 1, page_size: 25, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  const eventOptions = useMemo(() => events.filter((item) => !module || item.module === module), [events, module]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    const params = {
      from_date: fromDate || undefined,
      to_date: toDate || undefined,
      module: module || undefined,
      event_key: eventKey || undefined,
      channel: channel || undefined,
      status: status || undefined,
      recipient_user_id: recipientUserId || undefined,
      search: search.trim() || undefined,
      page,
      page_size: 25,
    };
    listNotificationHistory(params)
      .then((response) => { if (active) setHistory(responseData(response) || { items: [], recipient_options: [], total: 0, page: 1, page_size: 25, pages: 0 }); })
      .catch((requestError) => { if (active) setError(extractError(requestError, "Unable to load notification history.")); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [fromDate, toDate, module, eventKey, channel, status, recipientUserId, search, page]);

  const selectRange = (value) => {
    setRange(value);
    setPage(1);
    if (value !== "custom") {
      const dates = presetDates(Number(value));
      setFromDate(dates.from);
      setToDate(dates.to);
    }
  };
  const clearFilters = () => {
    const dates = presetDates(7);
    setRange("7");
    setFromDate(dates.from);
    setToDate(dates.to);
    setModule("");
    setEventKey("");
    setChannel("");
    setStatus("");
    setRecipientUserId(null);
    setSearch("");
    setPage(1);
  };
  const filterChange = (setter) => (changeEvent) => { setter(changeEvent.target.value); setPage(1); };

  return (
    <section aria-label="Notification History" style={{ ...panel, padding: 14, display: "grid", gap: 14 }}>
      <div>
        <h2 style={{ margin: "0 0 5px" }}>Notification History</h2>
        <p style={{ margin: 0, color: colors.textMuted }}>Tenant-scoped email delivery and in-app creation activity. Rendered message content is never shown.</p>
      </div>
      <div aria-label="History date range" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {[{ value: "1", label: "Today" }, { value: "7", label: "Last 7 days" }, { value: "30", label: "Last 30 days" }, { value: "custom", label: "Custom range" }].map((option) => <Button key={option.value} type="button" variant={range === option.value ? "primary" : "secondary"} onClick={() => selectRange(option.value)}>{option.label}</Button>)}
      </div>
      {range === "custom" ? <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}><label style={{ fontWeight: 700 }}>From date<input aria-label="History from date" type="date" value={fromDate} max={toDate} onChange={filterChange(setFromDate)} style={inputStyle} /></label><label style={{ fontWeight: 700 }}>To date<input aria-label="History to date" type="date" value={toDate} min={fromDate} onChange={filterChange(setToDate)} style={inputStyle} /></label></div> : null}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
        <label style={{ fontWeight: 700 }}>Module<select aria-label="History module" value={module} onChange={(changeEvent) => { setModule(changeEvent.target.value); setEventKey(""); setPage(1); }} style={inputStyle}><option value="">All modules</option>{MODULES.slice(1).map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
        <label style={{ fontWeight: 700 }}>Event<select aria-label="History event" value={eventKey} onChange={filterChange(setEventKey)} style={inputStyle}><option value="">All events</option>{eventOptions.map((item) => <option key={item.event_key} value={item.event_key}>{item.name}</option>)}</select></label>
        <label style={{ fontWeight: 700 }}>Channel<select aria-label="History channel" value={channel} onChange={filterChange(setChannel)} style={inputStyle}><option value="">All channels</option><option value="email">Email</option><option value="in_app">In-app</option></select></label>
        <label style={{ fontWeight: 700 }}>Status<select aria-label="History status" value={status} onChange={filterChange(setStatus)} style={inputStyle}><option value="">All statuses</option>{Object.entries(HISTORY_STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label style={{ fontWeight: 700 }}>Recipient<RecipientCombobox options={history.recipient_options || []} value={recipientUserId} onChange={(nextValue) => { setRecipientUserId(nextValue); setPage(1); }} /></label>
        <label style={{ fontWeight: 700 }}>Search<input aria-label="Search notification history" value={search} onChange={filterChange(setSearch)} placeholder="Event, record or recipient" style={inputStyle} /></label>
      </div>
      <div><Button type="button" variant="secondary" onClick={clearFilters}>Clear filters</Button></div>
      {error ? <div role="alert" style={{ color: colors.danger, background: colors.dangerSubtle, padding: 10, borderRadius: radius.sm }}>{error}</div> : null}
      {loading ? <div style={{ color: colors.textMuted, padding: 14 }}>Loading notification history...</div> : history.items.length ? <>
        <div style={{ overflowX: "auto" }}><table style={{ width: "100%", minWidth: 960, borderCollapse: "collapse", fontSize: 13 }}><thead><tr style={{ textAlign: "left", background: colors.tableHeader }}>{["Date / Time", "Event", "Module", "Recipient", "Channel", "Status", "Record", ""].map((heading, index) => <th key={`${heading}-${index}`} scope="col" style={{ padding: "10px 9px" }}>{heading}</th>)}</tr></thead><tbody>{history.items.map((item) => <tr key={item.id} style={{ borderTop: `1px solid ${colors.border}` }}><td style={{ padding: 9, whiteSpace: "nowrap" }}>{displayDateTime(item.created_at)}</td><td style={{ padding: 9 }}><strong>{item.event_name}</strong>{occurrenceLabel(item.scheduled_occurrence) ? <div style={{ color: colors.textMuted, fontSize: 11 }}>{occurrenceLabel(item.scheduled_occurrence)}</div> : null}</td><td style={{ padding: 9 }}>{item.module}</td><td style={{ padding: 9 }}>{item.recipient_name || item.recipient_email_masked || "Unavailable"}{item.recipient_name && item.recipient_email_masked ? <div style={{ color: colors.textMuted, fontSize: 11 }}>{item.recipient_email_masked}</div> : null}</td><td style={{ padding: 9 }}>{item.channel === "email" ? "Email" : "In-app"}</td><td style={{ padding: 9 }}><HistoryStatus status={item.status} /></td><td style={{ padding: 9 }}>{item.record_link ? <a href={item.record_link}>{item.record_reference}</a> : item.record_reference || "Not available"}</td><td style={{ padding: 9 }}><Button type="button" variant="ghost" onClick={() => setDetail(item)} aria-label={`View history details for ${item.event_name}`}>View</Button></td></tr>)}</tbody></table></div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}><span style={{ color: colors.textMuted }}>{history.total} result{history.total === 1 ? "" : "s"} · Page {history.page} of {history.pages}</span><div style={{ display: "flex", gap: 8 }}><Button type="button" variant="secondary" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Previous</Button><Button type="button" variant="secondary" disabled={page >= history.pages} onClick={() => setPage((current) => current + 1)}>Next</Button></div></div>
      </> : <div style={{ padding: 28, textAlign: "center", color: colors.textMuted, background: colors.surfaceSubtle, borderRadius: radius.md }}><strong style={{ color: colors.textPrimary }}>No notifications found for this period.</strong><div style={{ marginTop: 5 }}>Try a wider date range or clear the filters.</div></div>}
      <ModalA11yWrapper open={Boolean(detail)} onClose={() => setDetail(null)} labelledBy="notification-history-detail-title" containerStyle={{ width: "min(680px, calc(100vw - 32px))" }}>
        {detail ? <div style={{ display: "grid", gap: 14, padding: 4 }}><div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}><div><div style={{ color: colors.brandPrimary, fontSize: 11, fontWeight: 800, textTransform: "uppercase" }}>Notification history</div><h2 id="notification-history-detail-title" style={{ margin: "5px 0 0" }}>{detail.event_name}</h2></div><Button type="button" variant="ghost" onClick={() => setDetail(null)} aria-label="Close history details">Close</Button></div><dl style={{ display: "grid", gridTemplateColumns: "minmax(150px, 1fr) 2fr", gap: "9px 14px", margin: 0 }}><dt>Event</dt><dd style={{ margin: 0 }}>{detail.event_key}</dd><dt>Record</dt><dd style={{ margin: 0 }}>{detail.record_reference || "Not available"}</dd><dt>Channel</dt><dd style={{ margin: 0 }}>{detail.channel === "email" ? "Email" : "In-app"}</dd><dt>Recipient</dt><dd style={{ margin: 0 }}>{detail.recipient_name || "Unavailable"}{detail.recipient_email_masked ? ` (${detail.recipient_email_masked})` : ""}</dd><dt>Status</dt><dd style={{ margin: 0 }}><HistoryStatus status={detail.status} /></dd><dt>Template version</dt><dd style={{ margin: 0 }}>{detail.template_version_id || "System default / not recorded"}</dd><dt>Created / queued</dt><dd style={{ margin: 0 }}>{displayDateTime(detail.queued_at || detail.created_at)}</dd><dt>Last attempted</dt><dd style={{ margin: 0 }}>{displayDateTime(detail.attempted_at)}</dd><dt>Completed</dt><dd style={{ margin: 0 }}>{displayDateTime(detail.completed_at)}</dd><dt>Attempt count</dt><dd style={{ margin: 0 }}>{detail.attempt_count}</dd>{detail.failure_reason ? <><dt>Failure reason</dt><dd style={{ margin: 0 }}>{detail.failure_reason}</dd></> : null}{detail.provider ? <><dt>Provider</dt><dd style={{ margin: 0 }}>{detail.provider}</dd></> : null}{detail.provider_reference ? <><dt>Provider reference</dt><dd style={{ margin: 0 }}>{detail.provider_reference}</dd></> : null}{occurrenceLabel(detail.scheduled_occurrence) ? <><dt>Scheduled context</dt><dd style={{ margin: 0 }}>{occurrenceLabel(detail.scheduled_occurrence)}</dd></> : null}</dl><div style={{ color: colors.textMuted, fontSize: 12 }}>Rendered email and in-app message bodies are intentionally excluded from history.</div></div> : null}
      </ModalA11yWrapper>
    </section>
  );
}

export default function NotificationsAdmin() {
  const [activeTab, setActiveTab] = useState("configuration");
  const [events, setEvents] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [expandedKey, setExpandedKey] = useState(null);
  const [timingKey, setTimingKey] = useState(null);
  const [moduleFilter, setModuleFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [rowFeedback, setRowFeedback] = useState({});
  const [pageMessage, setPageMessage] = useState(null);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [messageEditorKey, setMessageEditorKey] = useState(null);
  const [messageDraft, setMessageDraft] = useState(null);
  const [messageSaving, setMessageSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editorFeedback, setEditorFeedback] = useState(null);
  const [insertionTarget, setInsertionTarget] = useState("body_template");
  const subjectRef = useRef(null);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setPageMessage(null);
    try {
      const data = responseData(await listNotificationEventConfigurations()) || [];
      setEvents(data);
      setDrafts(Object.fromEntries(data.map((item) => [item.event_key, eventDraft(item)])));
    } catch (error) {
      setPageMessage({ tone: "error", text: extractError(error, "Unable to load notification settings.") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const expandedEvent = useMemo(() => events.find((item) => item.event_key === expandedKey) || null, [events, expandedKey]);
  const timingEvent = useMemo(() => events.find((item) => item.event_key === timingKey) || null, [events, timingKey]);
  const messageEvent = useMemo(() => events.find((item) => item.event_key === messageEditorKey) || null, [events, messageEditorKey]);
  const emailDeliveryActive = Boolean(events[0]?.email_delivery_available);

  useEffect(() => {
    const editorKey = timingKey || expandedKey;
    const draft = editorKey ? drafts[editorKey] : null;
    const needsNamedUsers = draft?.recipients?.some((rule) => rule.type === "named_users") || draft?.timing?.escalations?.some((escalation) => escalation.recipients.some((rule) => rule.type === "named_users"));
    if (!editorKey || !needsNamedUsers) {
      setEligibleUsers([]);
      return;
    }
    listEligibleNotificationUsers(editorKey).then((response) => setEligibleUsers(responseData(response) || [])).catch(() => setEligibleUsers([]));
  }, [expandedKey, timingKey, drafts]);

  const filteredEvents = useMemo(() => {
    const term = search.trim().toLowerCase();
    return events.filter((item) => (moduleFilter === "All" || item.module === moduleFilter) && (!term || item.name.toLowerCase().includes(term) || item.event_key.toLowerCase().includes(term)));
  }, [events, moduleFilter, search]);

  const replaceEvent = (updated) => setEvents((current) => current.map((item) => item.event_key === updated.event_key ? updated : item));
  const changeDraft = (eventKey, change) => {
    setDrafts((current) => ({ ...current, [eventKey]: typeof change === "function" ? change(current[eventKey]) : change }));
    setRowFeedback((current) => ({ ...current, [eventKey]: null }));
  };

  const saveRow = async (eventKey) => {
    setSavingKey(eventKey);
    setRowFeedback((current) => ({ ...current, [eventKey]: null }));
    try {
      const updated = responseData(await updateNotificationEventConfiguration(eventKey, configurationPayload(drafts[eventKey])));
      replaceEvent(updated);
      setDrafts((current) => ({ ...current, [eventKey]: eventDraft(updated) }));
      setRowFeedback((current) => ({ ...current, [eventKey]: { tone: "success", text: "Saved" } }));
    } catch (error) {
      setRowFeedback((current) => ({ ...current, [eventKey]: { tone: "error", text: extractError(error, "Save failed") } }));
    } finally {
      setSavingKey(null);
    }
  };

  const openMessageEditor = (item) => {
    setMessageEditorKey(item.event_key);
    setMessageDraft({ subject_template: item.template.subject_template, body_template: item.template.body_template });
    setInsertionTarget("body_template");
    setPreview(null);
    setEditorFeedback(null);
  };
  const closeMessageEditor = () => {
    if (messageSaving) return;
    setMessageEditorKey(null);
    setMessageDraft(null);
    setPreview(null);
    setEditorFeedback(null);
  };
  const validateMessage = () => {
    if (!messageDraft?.subject_template.trim() || !messageDraft?.body_template.trim()) {
      setEditorFeedback({ tone: "error", text: "Subject and body are required." });
      return false;
    }
    const invalid = invalidTemplateTokens(messageDraft.subject_template, messageDraft.body_template, messageEvent.supported_variables);
    if (invalid.length) {
      setEditorFeedback({ tone: "error", text: `Unsupported placeholder or expression: ${invalid.join(", ")}. Use an approved placeholder.` });
      return false;
    }
    return true;
  };
  const runPreview = async () => {
    setEditorFeedback(null);
    if (!validateMessage()) return;
    setPreviewing(true);
    try {
      setPreview(responseData(await previewNotificationTemplate(messageEditorKey, messageDraft)));
    } catch (error) {
      setEditorFeedback({ tone: "error", text: extractError(error, "Unable to preview this template.") });
    } finally {
      setPreviewing(false);
    }
  };
  const mergeSavedTemplate = (updated) => {
    replaceEvent(updated);
    setDrafts((current) => ({
      ...current,
      [updated.event_key]: {
        ...(current[updated.event_key] || eventDraft(updated)),
        subject_template: updated.template.subject_template,
        body_template: updated.template.body_template,
      },
    }));
    setMessageDraft({ subject_template: updated.template.subject_template, body_template: updated.template.body_template });
  };
  const saveMessage = async () => {
    setEditorFeedback(null);
    if (!validateMessage()) return;
    setMessageSaving(true);
    try {
      const updated = responseData(await updateNotificationEventConfiguration(messageEditorKey, {
        ...configurationPayload(eventDraft(messageEvent)),
        ...messageDraft,
      }));
      mergeSavedTemplate(updated);
      setEditorFeedback({ tone: "success", text: "Message saved as a new template version." });
    } catch (error) {
      setEditorFeedback({ tone: "error", text: extractError(error, "Unable to save this message.") });
    } finally {
      setMessageSaving(false);
    }
  };
  const restoreDefault = async () => {
    setMessageSaving(true);
    setEditorFeedback(null);
    try {
      const updated = responseData(await restoreNotificationTemplateDefault(messageEditorKey));
      mergeSavedTemplate(updated);
      setPreview(null);
      setEditorFeedback({ tone: "success", text: "The Glyder default message was restored. Delivery, recipients and timing were unchanged." });
    } catch (error) {
      setEditorFeedback({ tone: "error", text: extractError(error, "Unable to restore the default message.") });
    } finally {
      setMessageSaving(false);
    }
  };
  const insertPlaceholder = (variable) => {
    if (!messageEvent?.supported_variables.includes(variable)) return;
    const placeholder = `{{${variable}}}`;
    setMessageDraft((current) => ({ ...current, [insertionTarget]: `${current[insertionTarget] || ""}${placeholder}` }));
    setEditorFeedback(null);
  };

  if (loading) return <div style={{ padding: 20, color: colors.textMuted }}>Loading notification configuration...</div>;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <section style={{ ...panel, padding: 18 }}>
        <div style={{ color: colors.brandPrimary, textTransform: "uppercase", letterSpacing: ".08em", fontSize: 12, fontWeight: 700 }}>Admin / Notifications</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <h2 style={{ margin: "6px 0" }}>Notifications administration</h2>
          <span aria-label="Email delivery status" style={{ borderRadius: radius.pill, padding: "6px 10px", fontSize: 12, fontWeight: 700, color: emailDeliveryActive ? colors.success : colors.warning, border: `1px solid ${emailDeliveryActive ? colors.successBorder : colors.warningBorder}`, background: emailDeliveryActive ? colors.successSubtle : colors.warningSubtle }}>Email delivery: {emailDeliveryActive ? "Active" : "Not configured"}</span>
        </div>
        <p style={{ margin: 0, color: colors.textMuted, lineHeight: 1.55 }}>Configure tenant-specific events, recipients and safe email templates. {emailDeliveryActive ? "Email delivery is active; previews still never send a message." : "Email delivery is not configured in this environment. Templates can still be edited and previewed; saving never sends an email."}</p>
      </section>

      <div role="tablist" aria-label="Notifications administration" style={{ display: "flex", gap: 8, borderBottom: `1px solid ${colors.border}` }}>
        <Button type="button" role="tab" aria-selected={activeTab === "configuration"} variant={activeTab === "configuration" ? "primary" : "ghost"} onClick={() => setActiveTab("configuration")}>Configuration</Button>
        <Button type="button" role="tab" aria-selected={activeTab === "history"} variant={activeTab === "history" ? "primary" : "ghost"} onClick={() => setActiveTab("history")}>Notification History</Button>
      </div>

      {activeTab === "configuration" ? <>
        {pageMessage ? <div role="alert" style={{ padding: 12, borderRadius: radius.sm, color: pageMessage.tone === "error" ? colors.danger : colors.success, background: pageMessage.tone === "error" ? colors.dangerSubtle : colors.successSubtle }}>{pageMessage.text}</div> : null}

      <section style={{ ...panel, padding: 14, display: "grid", gap: 12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{MODULES.map((module) => <Button key={module} type="button" variant={moduleFilter === module ? "primary" : "secondary"} onClick={() => setModuleFilter(module)}>{module}</Button>)}</div>
        <input aria-label="Search notification events" value={search} onChange={(changeEvent) => setSearch(changeEvent.target.value)} placeholder="Search events" style={inputStyle} />
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", minWidth: 1120, borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ textAlign: "left", background: colors.tableHeader }}>{["Event", "Module", "Status", "Email", "In-App", "Recipients", "Timing / Escalation", "Message", "Actions"].map((heading) => <th key={heading} scope="col" style={{ padding: "10px 9px" }}>{heading}</th>)}</tr></thead>
            <tbody>
              {filteredEvents.map((item) => {
                const draft = drafts[item.event_key] || eventDraft(item);
                const dirty = !sameDraft(draft, eventDraft(item));
                const expanded = expandedKey === item.event_key;
                const timingExpanded = timingKey === item.event_key;
                const saving = savingKey === item.event_key;
                const feedback = rowFeedback[item.event_key];
                const configurableReason = item.configurable ? undefined : "This canonical event is not configurable.";
                return (
                  <React.Fragment key={item.event_key}>
                    <tr style={{ borderTop: `1px solid ${colors.border}`, background: expanded || timingExpanded ? colors.tableRowSelected : colors.surface }}>
                      <td style={{ padding: 10, minWidth: 190 }}><strong>{item.name}</strong><div style={{ color: colors.textSubtle, fontSize: 11, marginTop: 3 }}>{item.event_key}</div></td>
                      <td style={{ padding: 10 }}>{item.module}</td>
                      <td style={{ padding: 8 }}><StatusToggle label="Status" checked={draft.enabled} disabled={!item.configurable || saving} disabledReason={configurableReason} onChange={(checked) => changeDraft(item.event_key, { ...draft, enabled: checked })} /></td>
                      <td style={{ padding: 8 }}><StatusToggle label="Email" checked={draft.email_enabled} disabled={!item.configurable || !item.permitted_channels.includes("email") || saving} disabledReason={!item.permitted_channels.includes("email") ? "Email is not available for this event." : configurableReason} onChange={(checked) => changeDraft(item.event_key, { ...draft, email_enabled: checked })} /></td>
                      <td style={{ padding: 8 }}><StatusToggle label="In-app" checked={draft.in_app_enabled} disabled={!item.configurable || !item.permitted_channels.includes("in_app") || saving} disabledReason={!item.permitted_channels.includes("in_app") ? "In-app notifications are not available for this event." : configurableReason} onChange={(checked) => changeDraft(item.event_key, { ...draft, in_app_enabled: checked })} /></td>
                      <td style={{ padding: 10, maxWidth: 180 }}>{recipientSummary(draft.recipients)}</td>
                      <td style={{ padding: 10, maxWidth: 220 }}>{item.timing_modes?.length ? <div style={{ display: "grid", gap: 6 }}><span>{timingSummary(draft.timing)}</span><Button type="button" variant="ghost" aria-expanded={timingExpanded} aria-controls={`timing-${item.event_key}`} onClick={() => { setTimingKey(timingExpanded ? null : item.event_key); setExpandedKey(null); }}>{timingSummary(draft.timing) === "Not configured" ? "Set up" : "Edit timing"}</Button></div> : <span style={{ color: colors.textSubtle }}>Not applicable</span>}</td>
                      <td style={{ padding: 8 }}>{item.permitted_channels.includes("email") ? <Button type="button" variant="secondary" onClick={() => openMessageEditor(item)} aria-label={`Edit message for ${item.name}`}>Edit message</Button> : <span style={{ color: colors.textSubtle }}>Not available</span>}</td>
                      <td style={{ padding: 8, minWidth: 130 }}><div style={{ display: "grid", gap: 6 }}><Button type="button" variant="ghost" aria-expanded={expanded} aria-controls={`settings-${item.event_key}`} onClick={() => { setExpandedKey(expanded ? null : item.event_key); setTimingKey(null); }}>{expanded ? "Hide recipients" : "Edit recipients"}</Button><Button type="button" variant="primary" disabled={!dirty || saving || !item.configurable} loading={saving} onClick={() => saveRow(item.event_key)}>{saving ? "Saving..." : "Save row"}</Button>{feedback ? <span role={feedback.tone === "error" ? "alert" : "status"} style={{ color: feedback.tone === "error" ? colors.danger : colors.success, fontSize: 12, fontWeight: 700 }}>{feedback.text}</span> : dirty ? <span style={{ color: colors.warning, fontSize: 11 }}>Unsaved changes</span> : null}</div></td>
                    </tr>
                    {expanded && expandedEvent && drafts[item.event_key] ? <tr style={{ borderTop: `1px solid ${colors.border}` }}><td colSpan={9} style={{ padding: 0 }}><EventSettingsEditor event={item} draft={drafts[item.event_key]} eligibleUsers={eligibleUsers} onChange={(nextDraft) => changeDraft(item.event_key, nextDraft)} onClose={() => setExpandedKey(null)} /></td></tr> : null}
                    {timingExpanded && timingEvent && drafts[item.event_key] ? <tr style={{ borderTop: `1px solid ${colors.border}` }}><td colSpan={9} style={{ padding: 0 }}><TimingEditor event={item} draft={drafts[item.event_key]} eligibleUsers={eligibleUsers} onChange={(nextDraft) => changeDraft(item.event_key, nextDraft)} onClose={() => setTimingKey(null)} onSave={() => saveRow(item.event_key)} saving={saving} dirty={dirty} /></td></tr> : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      </> : <NotificationHistory events={events} />}

      <ModalA11yWrapper open={Boolean(messageEvent && messageDraft)} onClose={closeMessageEditor} initialFocusRef={subjectRef} labelledBy="notification-message-editor-title" describedBy="notification-message-editor-description" containerStyle={{ width: "min(900px, calc(100vw - 32px))", padding: 0 }}>
        {messageEvent && messageDraft ? (
          <div style={{ display: "grid", gap: 16, padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}><div><div style={{ color: colors.brandPrimary, textTransform: "uppercase", letterSpacing: ".08em", fontSize: 11, fontWeight: 800 }}>Email message</div><h2 id="notification-message-editor-title" style={{ margin: "5px 0" }}>{messageEvent.name}</h2><p id="notification-message-editor-description" style={{ margin: 0, color: colors.textMuted }}>Edit the approved subject and body template. This does not send an email.</p></div><Button type="button" variant="ghost" onClick={closeMessageEditor} disabled={messageSaving} aria-label="Close message editor">Close</Button></div>
            {!emailDeliveryActive ? <div style={{ padding: 10, borderRadius: radius.sm, border: `1px solid ${colors.warningBorder}`, background: colors.warningSubtle, color: colors.warning, fontWeight: 700 }}>Email delivery: Not configured. You can still edit, preview and save this template.</div> : null}
            {editorFeedback ? <div role={editorFeedback.tone === "error" ? "alert" : "status"} style={{ padding: 10, borderRadius: radius.sm, color: editorFeedback.tone === "error" ? colors.danger : colors.success, background: editorFeedback.tone === "error" ? colors.dangerSubtle : colors.successSubtle }}>{editorFeedback.text}</div> : null}
            <label style={{ display: "grid", gap: 5, fontWeight: 700 }}>Subject<input ref={subjectRef} aria-label="Message subject" maxLength={500} value={messageDraft.subject_template} onFocus={() => setInsertionTarget("subject_template")} onChange={(changeEvent) => { setMessageDraft({ ...messageDraft, subject_template: changeEvent.target.value }); setEditorFeedback(null); }} style={inputStyle} /></label>
            <label style={{ display: "grid", gap: 5, fontWeight: 700 }}>Body<textarea aria-label="Message body" maxLength={20000} value={messageDraft.body_template} onFocus={() => setInsertionTarget("body_template")} onChange={(changeEvent) => { setMessageDraft({ ...messageDraft, body_template: changeEvent.target.value }); setEditorFeedback(null); }} rows={10} style={{ ...inputStyle, resize: "vertical" }} /></label>
            <div><div style={{ fontWeight: 700, fontSize: 13, marginBottom: 7 }}>Available placeholders</div><div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{messageEvent.supported_variables.map((variable) => <Button key={variable} type="button" variant="secondary" onClick={() => insertPlaceholder(variable)} aria-label={`Insert {{${variable}}} into ${insertionTarget === "subject_template" ? "subject" : "body"}`} style={{ borderRadius: radius.pill, minHeight: 32, padding: "5px 9px", color: colors.brandPrimary, background: colors.accentSubtle, borderColor: colors.accentBorder }}>{`{{${variable}}}`}</Button>)}</div><div style={{ color: colors.textMuted, fontSize: 12, marginTop: 6 }}>Focus Subject or Body, then select a placeholder to insert it. Other template expressions are blocked.</div></div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><Button type="button" variant="secondary" loading={previewing} disabled={messageSaving} onClick={runPreview}>{previewing ? "Previewing..." : "Preview with sample data"}</Button><Button type="button" variant="secondary" disabled={messageSaving || previewing} onClick={restoreDefault}>Restore default</Button><span style={{ alignSelf: "center", color: colors.textMuted, fontSize: 12 }}>Template version {messageEvent.template.version_number || "system default"}</span></div>
            {preview ? <div aria-label="Template preview" style={{ ...panel, padding: 16, background: colors.surfaceSubtle }}><div style={{ color: colors.warning, fontSize: 12, marginBottom: 8 }}>Representative sample data / No email was sent</div><div><strong>Subject:</strong> {preview.subject}</div><pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", lineHeight: 1.6, marginBottom: 0 }}>{preview.body}</pre></div> : null}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}><Button type="button" variant="secondary" disabled={messageSaving} onClick={closeMessageEditor}>Cancel</Button><Button type="button" variant="primary" loading={messageSaving} onClick={saveMessage}>{messageSaving ? "Saving..." : "Save message"}</Button></div>
          </div>
        ) : null}
      </ModalA11yWrapper>
    </div>
  );
}
